import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../services/api';

const HistoryContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const HistoryHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const ConversationList = styled(List)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: 0,
}));

const ConversationItem = styled(ListItem)(({ theme, selected }) => ({
  padding: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ConversationButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1.5),
  flexDirection: 'column',
  alignItems: 'flex-start',
}));

const ConversationPreview = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
  marginTop: theme.spacing(0.5),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

const ConversationHistory = ({ 
  selectedConversationId, 
  onConversationSelect, 
  onNewConversation, 
  onRefresh 
}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.assistantApi.getConversations(20);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Failed to load conversation history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const handleRefresh = () => {
    fetchConversations();
    if (onRefresh) {
      onRefresh();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-GB');
    }
  };

  const getConversationTitle = (conversation) => {
    if (conversation.LastUserMessage) {
      // Take first 50 characters for title
      const message = conversation.LastUserMessage;
      return message.length > 50 ? message.substring(0, 50) + '...' : message;
    }
    return 'New Conversation';
  };

  return (
    <HistoryContainer>
      <HistoryHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatIcon />
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            Chat History
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </HistoryHeader>

      {error && (
        <Alert severity="error" sx={{ m: 1 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <ConversationList>
          {conversations.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No conversations yet. Start a new chat!
              </Typography>
            </Box>
          ) : (
            conversations.map((conversation) => (
              <ConversationItem
                key={conversation.ConversationID}
                selected={selectedConversationId === conversation.ConversationID}
              >
                <ConversationButton
                  onClick={() => onConversationSelect(conversation.ConversationID)}
                >
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ 
                        fontWeight: selectedConversationId === conversation.ConversationID ? 600 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        marginRight: 1
                      }}
                    >
                      {getConversationTitle(conversation)}
                    </Typography>
                    <Chip
                      label={conversation.MessageCount}
                      size="small"
                      sx={{ 
                        height: 18, 
                        fontSize: '0.7rem',
                        minWidth: 20
                      }}
                    />
                  </Box>
                  
                  {conversation.LastUserMessage && (
                    <ConversationPreview>
                      {conversation.LastUserMessage}
                    </ConversationPreview>
                  )}
                  
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(conversation.LastMessageDate)}
                    </Typography>
                  </Box>
                </ConversationButton>
              </ConversationItem>
            ))
          )}
        </ConversationList>
      )}
    </HistoryContainer>
  );
};

export default ConversationHistory;
